/**
 * Type definitions for Scouting Ops Site database
 * These complement the auto-generated Supabase types
 */

import { Json } from '@/integrations/supabase/types';

// Matches src/lib/seed-content.ts's byUnitType exactly — that file has
// carried real seed content for all 9 of these since before this type
// caught up (see 20260716090000_org_profiles_unit_type_widen.sql). Spans
// 4 organizations, not just Scouting America — this platform's content
// model already assumed multi-organization support.
export type UnitType =
  | 'cub_scout_pack'
  | 'scouts_bsa_troop'
  | 'venturing_crew'
  | 'sea_scout_ship'
  | 'exploring_post'
  | 'exploring_club'
  | 'girl_scout_troop'
  | 'trail_life_troop'
  | 'ahg_troop';
export type SiteStatus = 'draft' | 'published' | 'has_changes';
export type BlockType = 'hero' | 'text' | 'image' | 'calendar' | 'contact' | 'button' | 'list' | 'leadership' | 'unit-identity';

// A page/block can be published yet still restricted to org members
// ('internal') independent of is_published/status — see
// docs/site-builder-overhaul.md's Phase A. A block is only effectively
// public if both its page and the block itself are 'public'.
export type ContentVisibility = 'public' | 'internal';

// Every typeface offered in Site Styles' Fonts screen — a small, curated
// set (not "any Google Font") so every option is guaranteed to actually
// load and pair sensibly. 'system'/'georgia' need no web-font request; the
// rest are loaded on demand (see font-catalog.ts) only for the families a
// site actually uses.
export type SiteFontFamily = 'system' | 'georgia' | 'marcellus' | 'playfair-display' | 'poppins' | 'open-sans' | 'inter' | 'roboto' | 'lato';

// The five palette slots every "section color theme" (Lightest/Light/Dark/
// Darkest) is derived from — same model as the Colors screen's swatch
// strip. Named by tone, not role (no "primary"/"secondary"), so the same
// slot can be reused consistently across backgrounds, text, and accents.
export interface SiteThemePalette {
  lightest: string;
  light: string;
  accent: string;
  dark: string;
  darkest: string;
}

// The button font deliberately isn't a fully independent 4th typeface —
// "unnecessary stylistic risk" per the design-system pass; it inherits the
// heading or paragraph family by default, with an explicit-family escape
// hatch still available for a unit that really wants a 3rd typeface.
export type ButtonFontSetting = 'inherit-heading' | 'inherit-body' | SiteFontFamily;

export interface SiteThemeFonts {
  headingFont: SiteFontFamily;
  paragraphFont: SiteFontFamily;
  buttonFont: ButtonFontSetting;
  miscFont: SiteFontFamily;
  baseSize: number;
}

export type ButtonVariantStyle = 'contained' | 'outlined' | 'text';
// 4-step shape scale (was 3 — added 'soft', a lighter rounding than
// 'rounded') shared by button shape and form field shape.
export type ShapeScale = 'square' | 'soft' | 'rounded' | 'pill';

export interface SiteThemeButtonStyle {
  variant: ButtonVariantStyle;
}

export interface SiteThemeButtons {
  // Global, not per-slot — "mixed shapes weaken system coherence" per the
  // design-system pass. Was SiteThemeButtonStyle.shape on each of the 3
  // slots; moved here in the same migration that added 'soft'.
  shape: ShapeScale;
  primary: SiteThemeButtonStyle;
  secondary: SiteThemeButtonStyle;
  tertiary: SiteThemeButtonStyle;
}

export type FormBorderStyle = 'none' | 'outline';
export type FormPadding = 'sm' | 'md' | 'lg';

export interface SiteThemeForms {
  fieldShape: ShapeScale;
  fieldFill: boolean;
  border: FormBorderStyle;
  padding: FormPadding;
}

export type SurfaceDensity = 'compact' | 'comfortable' | 'spacious';

// Deliberately scoped to just density for now — card style, image style,
// nav style, and decorative treatment are real, documented follow-ups
// (see docs/site-builder-overhaul.md) that each need their own
// component-level plumbing beyond the theme config itself.
export interface SiteThemeSurfaces {
  density: SurfaceDensity;
}

export interface SiteThemeConfig {
  palette: SiteThemePalette;
  fonts: SiteThemeFonts;
  buttons: SiteThemeButtons;
  forms: SiteThemeForms;
  surfaces: SiteThemeSurfaces;
}

export interface Site {
  id: string;
  clerk_org_id: string;
  status: SiteStatus;
  published_at: string | null;
  seo_title: string | null;
  seo_description: string | null;
  theme_config: SiteThemeConfig;
  logo_url: string | null;
  // The one opt-in switch for the Calendar module's real-data binding —
  // see docs/site-builder-overhaul.md's Phase B "public site data gateway".
  // Off by default; publishing the site itself does not imply this.
  calendar_visible_on_site: boolean;
  // Whether the visitor-facing accessibility preferences launcher/panel
  // appears on the published site — see src/lib/accessibility/. Everything
  // else about that feature is an unauthenticated, visitor-local
  // localStorage preference and has no column here.
  accessibility_launcher_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface Page {
  id: string;
  site_id: string;
  title: string;
  slug: string;
  page_order: number;
  parent_page_id: string | null;
  show_in_nav: boolean;
  seo_title: string | null;
  seo_description: string | null;
  is_published: boolean;
  visibility: ContentVisibility;
  // At most one page per site — enforced by a partial unique index
  // (`pages_one_home_per_site_idx`), not just app logic. Determines what
  // renders at the site's root URL (see resolveCurrentPage in page-tree.ts)
  // instead of the old hardcoded slug === 'home' convention.
  is_home: boolean;
  created_at: string;
  updated_at: string;
}

// A "den leader"-style delegated grant: lets either a specific Clerk org
// member OR a whole security group manage one page's content blocks
// without the broader website_content:manage permission — exactly one of
// clerk_user_id/security_group_id is set, never both (see the Phase E
// migration's CHECK constraint). Group-based grants follow whoever
// currently belongs to that group (managed in Scouting Ops Hub), so access
// survives a leadership handoff without anyone updating this row by hand.
// See page_editors RLS for the exact scope (blocks only, not the page row
// or site settings).
export interface PageEditor {
  id: string;
  page_id: string;
  clerk_user_id: string | null;
  security_group_id: string | null;
  created_at: string;
  created_by_clerk_user_id: string;
}

export interface OrgSecurityGroup {
  id: string;
  name: string;
}

// One block, as captured in a page_versions snapshot — deliberately a
// separate (looser) shape from Block: no id/timestamps, since those
// aren't meaningful to restore (see
// supabase/migrations/20260715150000_page_versions.sql).
export interface PageVersionBlock {
  block_type: BlockType;
  content: Json;
  block_order: number;
  row_order: number;
  col_span: number;
  visibility: ContentVisibility;
}

// Auto-captured every time a page transitions into published — "every
// publish is a restore point" (docs/site-builder-overhaul.md's Phase E).
export interface PageVersion {
  id: string;
  page_id: string;
  site_id: string;
  page_title: string;
  blocks_snapshot: PageVersionBlock[];
  created_at: string;
}

export interface Block {
  id: string;
  page_id: string;
  block_type: BlockType;
  content: Json;
  block_order: number;
  row_order: number;
  col_span: number;
  visibility: ContentVisibility;
  created_at: string;
  updated_at: string;
}

// Block content types (for type hints, content is stored as Json)
export interface HeroBlockContent {
  headline: string;
  subheadline?: string;
  imageUrl?: string;
  buttonText?: string;
  buttonLink?: string;
}

// variant/align mirror MUI Typography's own prop names directly — this is
// a "component" block, so configuring it means configuring the real MUI
// component, not a bespoke vocabulary translated into one.
export interface TextBlockContent {
  content: string;
  variant?: 'body1' | 'body2' | 'h4' | 'h5' | 'h6';
  align?: 'left' | 'center' | 'right';
}

export interface ImageBlockContent {
  imageUrl: string;
  caption?: string;
  alt?: string;
  objectFit?: 'cover' | 'contain';
}

// No manually-typed event list anymore — the Calendar widget is bound to
// real scoutingops-calendar data (see get_public_site_calendar_events),
// gated by the unit opting in via sites.calendar_visible_on_site. Content
// is display config only.
export interface CalendarBlockContent {
  heading?: string;
}

// Adults only, name + position title — never raw contact info (see
// get_public_site_leadership). "Contact this leader" goes through the
// existing masked contact block, not a rendered email/phone.
export interface LeadershipBlockContent {
  heading?: string;
}

export interface UnitIdentityBlockContent {
  showMeetingSchedule?: boolean;
}

// Row shapes returned by the public site data gateway RPCs (see
// supabase/migrations/20260715000000_public_site_data_gateway.sql) — not
// stored block content, just the query result shape.
export interface PublicLeader {
  contact_id: string;
  first_name: string;
  last_name: string;
  position_title: string;
  position_sort_order: number;
}

export interface PublicMeetingSchedule {
  schedule_id: string;
  event_type_name: string;
  classifier: 'in_person' | 'virtual';
  day_of_week: 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';
  meeting_time: string;
  location_name: string | null;
  formatted_address: string | null;
}

export interface PublicCalendarEvent {
  event_id: string;
  title: string;
  start_at: string;
  end_at: string | null;
  all_day: boolean;
  location_name: string | null;
}

export interface ContactBlockContent {
  heading?: string;
  description?: string;
  showName?: boolean;
  showEmail?: boolean;
  showMessage?: boolean;
}

// variant/color are MUI Button's own prop names — see the note on
// TextBlockContent above.
export interface ButtonBlockContent {
  text: string;
  link: string;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'primary' | 'secondary' | 'inherit';
}

export interface ListBlockContent {
  heading?: string;
  items: string[];
  dense?: boolean;
}

// Helper to extract typed content from block
export function getBlockContent<T>(block: Block): T {
  return block.content as unknown as T;
}
